import { useCallback } from "react";
import { useBaseAction } from "hooks/core/index";
import {
  ICustomReactSelectOption,
  ISelectOption,
} from "core/types/elements/ICustomReactSelectOption";
import { createItemLabel } from "core/utils/string";

type TProps<T> = {
  url: string;
  tokenValue?: string;
  filter?: string;
  expand?: string;
  orderby?: string;
  formatLabel?: (item: T) => string;
  maxResults?: number;
};

const useAsyncSelectOptions = <T extends ISelectOption>({
  url,
  tokenValue,
  filter,
  expand,
  orderby,
  formatLabel,
  maxResults = 100,
}: TProps<T>) => {
  const { request } = useBaseAction();

  const mappingOptions = (data: T[]): ICustomReactSelectOption[] => {
    return data.map((item: T) => ({
      value: String(item?.id),
      label: formatLabel ? formatLabel(item) : createItemLabel(item),
      extraData: item,
    }));
  };

  const searchOptions = useCallback(
    async (searchTerm: string): Promise<ICustomReactSelectOption[]> => {
      // Don't search if less than 3 characters
      if (!searchTerm || searchTerm.trim().length < 3) {
        return [];
      }

      const searchFilter = `contains(tolower(name), '${searchTerm.toLowerCase().trim()}')`;
      const baseFilter = `isDeleted eq false${filter ? ` and ${filter}` : ""}`;
      const combinedFilter = `${baseFilter} and ${searchFilter}`;

      try {
        const data = (await request({
          method: "GET",
          url,
          tokenValue,
          useLoader: false,
          params: {
            $orderby: orderby || null,
            $filter: combinedFilter,
            $expand: expand || null,
            $top: maxResults,
          },
        })) as T[];

        if (Array.isArray(data)) {
          return mappingOptions(data);
        }
        return [];
      } catch (error) {
        console.error("Error fetching async select options:", error);
        return [];
      }
    },
    [url, tokenValue, filter, expand, orderby, formatLabel, maxResults, request],
  );

  return {
    searchOptions,
  };
};

export default useAsyncSelectOptions;
